import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { OverlayPage } from '@/components/overlay-page';
import { EmptyState } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAccounts } from '@/lib/accounts';
import { api } from '@/lib/api';
import { todayIso } from '@/lib/dates';
import { adjustmentProblems, quantityChange } from '@/lib/inventory-adjust';
import { useItems } from '@/lib/items';
import { useOrgId } from '@/lib/org';
import { useLanguage, useT } from '@/lib/i18n';
import { ltr, nameFor } from '@/lib/arabic';

export const Route = createFileRoute('/inventory/adjust')({
  component: Adjust,
  validateSearch: (s: Record<string, unknown>): { itemId?: string } =>
    typeof s['itemId'] === 'string' ? { itemId: s['itemId'] } : {},
});

interface Row {
  key: number;
  itemId: string;
  newQuantity: string;
}
let seq = 0;
/**
 * Screen IN2 (document 06; QBO + New › Inventory qty adjustment): the counted quantity per item;
 * the difference goes to the adjustment account at FIFO cost (decreases) or the latest cost
 * (increases).
 */
function Adjust() {
  const t = useT();
  const lang = useLanguage();
  const orgId = useOrgId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { itemId } = Route.useSearch();
  const items = useItems();
  const accounts = useAccounts();
  const stock = (items.data ?? []).filter((i) => i.kind === 'inventory');
  const adjustAccounts = (accounts.data ?? []).filter(
    (a) => a.class === 'expense' && !a.isControl && a.isActive,
  );
  const [date, setDate] = useState(todayIso());
  const [accountId, setAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [memo, setMemo] = useState('');
  const [rows, setRows] = useState<Row[]>(() => [
    { key: ++seq, itemId: itemId ?? '', newQuantity: '' },
    { key: ++seq, itemId: '', newQuantity: '' },
  ]);
  const [problems, setProblems] = useState<string[]>([]);
  const account =
    accountId ||
    adjustAccounts.find((a) => /shrink|adjust/i.test(a.name))?.id ||
    adjustAccounts.find((a) => a.detailType === 'cost_of_sales')?.id ||
    '';
  const used = rows.filter((r) => r.itemId);
  const save = useMutation({
    mutationFn: () =>
      api<{ lines: Array<{ itemName: string; change: string }> }>(
        `/orgs/${orgId}/inventory/adjustments`,
        {
          method: 'POST',
          body: JSON.stringify({
            date,
            adjustmentAccountId: account,
            ...(reference.trim() ? { reference: reference.trim() } : {}),
            ...(memo.trim() ? { memo: memo.trim() } : {}),
            lines: used.map((r) => ({ itemId: r.itemId, newQuantity: r.newQuantity.trim() })),
          }),
        },
      ),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      toast(
        t('salesPages.inventoryAdjust.adjusted', {
          list: r.lines
            .map((l) => `${l.itemName} ${lang === 'ar' ? ltr(l.change) : l.change}`)
            .join(t('salesPages.inventoryAdjust.listSeparator')),
        }),
      );
      void navigate({ to: '/sales/products-and-services' });
    },
  });
  if (!orgId) return <EmptyState title={t('shell.selectClientTitle')} />;
  const update = (key: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const submit = () => {
    const p = adjustmentProblems(account, rows, (id) => {
      const x = stock.find((i) => i.id === id);
      return x && nameFor(lang, x.name, x.nameAr);
    });
    setProblems(p);
    if (!p.length) save.mutate();
  };
  const close = () => void navigate({ to: '/sales/products-and-services' });
  return (
    <OverlayPage
      title={t('salesPages.inventoryAdjust.title')}
      onClose={close}
      footerLeft={
        <Button variant="outline" onClick={close}>
          {t('common.cancel')}
        </Button>
      }
      footerRight={
        <Button disabled={save.isPending} onClick={submit}>
          {save.isPending ? t('common.saving') : t('common.saveAndClose')}
        </Button>
      }
    >
      <div className="mx-auto flex max-w-[1100px] flex-col gap-5">
        <ErrorBanner>
          {problems.length || save.error ? (
            <ul className="list-disc ps-4">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
              {save.error ? <li>{save.error.message}</li> : null}
            </ul>
          ) : null}
        </ErrorBanner>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          <Field label={t('salesPages.inventoryAdjust.adjustmentDate')}>
            {(id) => <DateInput id={id} value={date} onChange={setDate} />}
          </Field>
          <Field
            label={t('salesPages.inventoryAdjust.account')}
            hint={t('salesPages.inventoryAdjust.accountHint')}
          >
            {(id) => (
              <NativeSelect id={id} value={account} onChange={(e) => setAccountId(e.target.value)}>
                <option value="">{t('salesPages.inventoryAdjust.chooseAccount')}</option>
                {adjustAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} · {nameFor(lang, a.fullName, a.fullNameAr)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('salesPages.inventoryAdjust.reference')}>
            {(id) => (
              <Input
                id={id}
                value={reference}
                maxLength={100}
                onChange={(e) => setReference(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div
          className="overflow-x-auto rounded-lg border bg-card"
          role="grid"
          aria-label={t('salesPages.inventoryAdjust.lines')}
        >
          <table className="w-full min-w-[720px] table-fixed text-sm">
            <thead className="border-b bg-muted/40 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-10 px-2 py-2">#</th>
                <th className="px-2 py-2">{t('salesPages.inventoryAdjust.colProduct')}</th>
                <th className="w-[160px] px-2 py-2 text-end">
                  {t('salesPages.inventoryAdjust.colQtyOnHand')}
                </th>
                <th className="w-[160px] px-2 py-2 text-end">
                  {t('salesPages.inventoryAdjust.colNewQty')}
                </th>
                <th className="w-[160px] px-2 py-2 text-end">
                  {t('salesPages.inventoryAdjust.colChange')}
                </th>
                <th className="w-10 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const item = stock.find((x) => x.id === r.itemId);
                const change = item
                  ? quantityChange(item.quantityOnHand ?? '0', r.newQuantity)
                  : null;
                return (
                  <tr key={r.key} className="border-b last:border-0">
                    <td className="px-2 py-1.5 text-muted-foreground">{i + 1}</td>
                    <td className="px-1 py-1.5">
                      <NativeSelect
                        aria-label={t('salesPages.inventoryAdjust.productLine', { n: i + 1 })}
                        value={r.itemId}
                        onChange={(e) => update(r.key, { itemId: e.target.value })}
                      >
                        <option value="" />
                        {stock.map((x) => (
                          <option key={x.id} value={x.id}>
                            {nameFor(lang, x.name, x.nameAr)}
                            {x.sku ? ` (${x.sku})` : ''}
                          </option>
                        ))}
                      </NativeSelect>
                    </td>
                    <td className="tnum px-2 py-1.5 text-end text-muted-foreground">
                      {item ? (item.quantityOnHand ?? '0') : ''}
                    </td>
                    <td className="px-1 py-1.5">
                      <Input
                        aria-label={t('salesPages.inventoryAdjust.newQuantityLine', { n: i + 1 })}
                        inputMode="decimal"
                        className="tnum text-end"
                        value={r.newQuantity}
                        onChange={(e) => update(r.key, { newQuantity: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Tab' && !e.shiftKey && i === rows.length - 1)
                            setRows((rs) => [...rs, { key: ++seq, itemId: '', newQuantity: '' }]);
                        }}
                      />
                    </td>
                    <td className="tnum px-2 py-1.5 text-end">
                      <bdi dir="ltr">{change ?? ''}</bdi>
                    </td>
                    <td className="px-1 py-1.5 text-center">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        tabIndex={-1}
                        aria-label={t('salesPages.inventoryAdjust.deleteLine', { n: i + 1 })}
                        onClick={() =>
                          setRows((rs) => {
                            const next = rs.filter((x) => x.key !== r.key);
                            return next.length
                              ? next
                              : [{ key: ++seq, itemId: '', newQuantity: '' }];
                          })
                        }
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRows((rs) => [...rs, { key: ++seq, itemId: '', newQuantity: '' }])}
          >
            {t('salesPages.inventoryAdjust.addLines')}
          </Button>
        </div>
        <Field label={t('salesPages.inventoryAdjust.memo')} className="max-w-xl">
          {(id) => (
            <Textarea
              id={id}
              value={memo}
              maxLength={1000}
              onChange={(e) => setMemo(e.target.value)}
            />
          )}
        </Field>
      </div>
    </OverlayPage>
  );
}
